/*
 * Pfanner — tray purchase guard.
 *
 * A tray ("8 × 1 L") has no stock feed of its own: the fulfiller syncs
 * quantities for the UNITS only, so every tray variant is inventory-untracked
 * and Shopify will happily sell an unlimited number of them. The PDP already
 * derives availability from the single's stock, but the PDP cannot see the cart —
 * it cannot know that 6 singles are already claiming stock from the same physical
 * pool — and it is a cached render that goes stale the moment stock moves.
 *
 * This closes those two cases. Modelled 1:1 on assets/pfanner-pfand.js — same
 * targets-in-Liquid pattern, same cart mechanics, same loop guards, same
 * section-rendering. Read that file first; this is the smaller sibling.
 *   Design: docs/plans/2026-08-14-tray-inventory-design.md
 *
 * HOW IT KNOWS THE LIMIT
 *   /cart.js exposes variant_id + quantity + key, but NOT the metafields the rule
 *   needs (units_per_pack) nor the paired single's stock. So Liquid — which CAN
 *   read both — pre-computes a per-tray-line limit into #pfanner-tray-limits
 *   (snippets/tray-limits.liquid), re-rendered with the cart on every change.
 *
 * WHAT IT DOES
 *   Any tray line over its limit is reduced to that limit, or removed when the
 *   limit is 0, and the shopper is told plainly which line changed and why —
 *   never a silent adjustment to something they are about to pay for.
 *
 *   It NEVER touches a single. Singles are inventory-tracked, so Shopify polices
 *   those lines itself; cutting one would fight Shopify and punish the wrong
 *   line. The tray is the unenforced half, so the tray is what gives way.
 *
 * WHY IT TERMINATES
 *   The limits are a pure function of the SINGLES' stock and the singles in the
 *   cart. Changing a tray line cannot change any limit, so one corrective pass
 *   always lands inside the limit and the next pass finds nothing to do — the
 *   same guard the deposit reconciler relies on ("targets exclude what we write"),
 *   plus a re-entrancy flag. Like that reconciler, this one renders sections
 *   directly and deliberately does NOT publish cartUpdate, so its own writes
 *   cannot re-trigger it.
 *
 * ⚠️ This is client-side, therefore bypassable, and it cannot block Shopify's
 * native checkout. A Cart & Checkout Validation Function would, but Functions
 * from a custom-distribution app require Shopify Plus and this store is not on
 * Plus — the same blocker that pushed Pfand to line items
 * (docs/plans/2026-08-05-pfand-plus-blocker.md). This closes the realistic cases
 * — stale page, mixed cart — not a determined bypass. That ceiling is accepted,
 * exactly as Pfand accepts it.
 */
(function () {
  'use strict';

  // Two holders exist, exactly as the deposit reconciler has: the cart PAGE
  // renders one and the DRAWER renders another, and on /cart both are in the DOM
  // at once — so they cannot share an id. Prefer the page's; fall back to the
  // drawer's, which is the one present on every other page.
  var LIMITS_IDS = ['pfanner-tray-limits', 'pfanner-tray-limits-drawer'];
  var NOTICE_CLASS = 'pf-tray-notice';
  var reconciling = false; // true while a change chain is in flight
  var debounceTimer = null;
  var pending = null;      // messages to show after our own render
  var lastMessages = null; // kept so a SIBLING re-render cannot silently erase the notice
  var lastShownAt = 0;
  var NOTICE_TTL = 12000;  // ms

  function limitsEl() {
    for (var i = 0; i < LIMITS_IDS.length; i += 1) {
      var el = document.getElementById(LIMITS_IDS[i]);
      if (el) return el;
    }
    return null;
  }

  function readLimits() {
    var el = limitsEl();
    if (!el) return null; // snippet not on this page → stay inert
    try {
      var list = JSON.parse(el.textContent || '[]');
      return Array.isArray(list) ? list : null;
    } catch (e) {
      return null;
    }
  }

  // Localised strings ride on the holder as data attributes, set by Liquid, so
  // the notice is a real translation rather than hardcoded German. The German
  // fallbacks matter only if the attributes are somehow missing.
  function strings() {
    var ds = (limitsEl() && limitsEl().dataset) || {};
    return {
      capped: ds.trayCapped || 'Nur noch {{ count }} × {{ title }} verfügbar – Menge angepasst.',
      removed: ds.trayRemoved || '{{ title }} ist derzeit nicht verfügbar und wurde entfernt.'
    };
  }

  function fill(tpl, vars) {
    return String(tpl).replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, k) {
      return Object.prototype.hasOwnProperty.call(vars, k) ? vars[k] : '';
    });
  }

  function fetchCart() {
    return fetch((window.routes ? window.routes.cart_url : '/cart') + '.js', {
      headers: { Accept: 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  // Lines over their limit. Empty in the overwhelmingly common case.
  function overLimit(cart, limits) {
    var byKey = {};
    limits.forEach(function (l) { byKey[l.key] = l; });
    var ops = [];
    (cart.items || []).forEach(function (item) {
      var lim = byKey[item.key];
      if (!lim) return; // no opinion: a single, an untracked partner, an unpaired tray
      var max = Number(lim.max);
      if (!isFinite(max) || max < 0) return;
      if (item.quantity > max) {
        ops.push({ key: item.key, qty: max, title: lim.title || item.title, removed: max === 0 });
      }
    });
    return ops;
  }

  // ── cart rendering (mirrors assets/pfanner-pfand.js) ──────────────────────
  function sectionsToRequest() {
    var list = ['cart-drawer', 'cart-icon-bubble'];
    var main = document.getElementById('main-cart-items');
    if (main && main.dataset.id) list.push(main.dataset.id);
    return list;
  }

  function renderSections(parsed) {
    var sections = (parsed && parsed.sections) || {};

    if (sections['cart-drawer']) {
      var ddoc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      ['cart-drawer-items', '.cart-drawer__footer'].forEach(function (sel) {
        var src = ddoc.querySelector(sel);
        var tgt = document.querySelector(sel);
        if (src && tgt) tgt.replaceWith(src);
      });
      // Same empty-state caveat as the deposit reconciler: those selectors miss
      // the drawer's own empty <-> not-empty switch. Reuse cart.js's helper.
      if (typeof window.pfannerSyncCartDrawerEmptyState === 'function') {
        window.pfannerSyncCartDrawerEmptyState(ddoc);
      }
    }

    var main = document.getElementById('main-cart-items');
    if (main && main.dataset.id && sections[main.dataset.id]) {
      var mdoc = new DOMParser().parseFromString(sections[main.dataset.id], 'text/html');
      var src = mdoc.querySelector('cart-items');
      var tgt = document.querySelector('cart-items');
      if (src && tgt) tgt.innerHTML = src.innerHTML;
    }

    if (sections['cart-icon-bubble']) {
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        var bdoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        var inner = bdoc.querySelector('.shopify-section') || bdoc.body;
        bubble.innerHTML = inner.innerHTML;
      }
    }
  }

  // `withSections` only on the LAST op, so the cart UI renders exactly once.
  function changeLine(op, withSections) {
    var body = { id: op.key, quantity: op.qty };
    if (withSections) {
      body.sections = sectionsToRequest();
      body.sections_url = window.location.pathname;
    }
    return fetch((window.routes ? window.routes.cart_change_url : '/cart/change') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  // Built on demand, never rendered empty and filled later: Dawn's base.css
  // carries a blanket `div:empty { display: none }`, so an empty placeholder
  // would be invisible the very day it is needed. Creating it only when there is
  // something to say sidesteps that rule entirely.
  // `isReassert` is set only by the re-assert ladder below. Without it each
  // re-assert would refresh lastShownAt and schedule three more timers, so the
  // notice would renew its own TTL for ever instead of expiring.
  function showNotice(messages, isReassert) {
    if (!messages || !messages.length) return;
    // Host order matters. Removing the cart's ONLY line empties the cart, and the
    // empty-cart view contains neither <cart-items> nor a limits holder — so the
    // first two candidates vanish in exactly the case where the explanation is
    // needed most ("why is my cart empty?"). Fall back to the section wrapper and
    // then <main>, which survive either way.
    var host = document.querySelector('cart-items') ||
               document.querySelector('cart-drawer-items') ||
               (limitsEl() && limitsEl().parentElement) ||
               document.getElementById('main-cart-items') ||
               document.querySelector('main');
    if (!host) return;

    var old = host.querySelector('.' + NOTICE_CLASS);
    if (old) old.remove();

    var box = document.createElement('div');
    box.className = NOTICE_CLASS;
    box.setAttribute('role', 'status');
    box.setAttribute('aria-live', 'polite');
    box.setAttribute('tabindex', '-1');
    messages.forEach(function (m) {
      var line = document.createElement('span');
      line.className = NOTICE_CLASS + '__line';
      line.textContent = m;
      box.appendChild(line);
    });
    host.insertBefore(box, host.firstChild);
    lastMessages = messages;
    if (isReassert) return; // keep the original timestamp, schedule nothing further
    lastShownAt = Date.now();

    // The deposit and gift reconcilers both re-render <cart-items> AFTER us — the
    // deposit one specifically because we publish cartUpdate so it can drop the
    // now-orphaned deposit line. Their render wipes this notice, and neither
    // publishes afterwards, so evaluate() is never called again to restore it.
    // Re-assert on a short ladder rather than relying on another cart event.
    [400, 1200, 2500].forEach(function (delay) {
      setTimeout(function () {
        if (!lastMessages || Date.now() - lastShownAt > NOTICE_TTL) return;
        if (document.querySelector('.' + NOTICE_CLASS)) return;
        showNotice(lastMessages, true);
      }, delay);
    });
    // Move focus so a keyboard or screen-reader user meets the change here,
    // rather than discovering a different quantity at checkout.
    try { box.focus({ preventScroll: true }); } catch (e) { /* older browsers */ }
  }

  function evaluate() {
    if (reconciling) return;
    var limits = readLimits();
    if (!limits) return;

    // A pending notice survives the re-render that follows our own write.
    if (pending && pending.length) {
      var msgs = pending;
      pending = null;
      showNotice(msgs);
    } else if (
      lastMessages &&
      Date.now() - lastShownAt < NOTICE_TTL &&
      !document.querySelector('.' + NOTICE_CLASS)
    ) {
      // Re-assert it: this cart region is re-rendered by OTHER reconcilers too
      // (the deposit and gift ones both replace <cart-items> innerHTML), and
      // whichever runs after us would otherwise wipe the explanation for a
      // quantity we just changed — leaving exactly the silent adjustment this
      // whole feature exists to avoid. Observed with the gift reconciler on
      // 2026-08-14. Time-boxed so it cannot haunt a later, unrelated cart.
      showNotice(lastMessages);
    }
    if (!limits.length) return;

    reconciling = true;
    fetchCart()
      .then(function (cart) {
        var ops = overLimit(cart, limits);
        if (!ops.length) return null;

        var s = strings();
        pending = ops.map(function (op) {
          return op.removed
            ? fill(s.removed, { title: op.title })
            : fill(s.capped, { count: op.qty, title: op.title });
        });

        // Sequential, never parallel — concurrent /cart writes race.
        var chain = Promise.resolve();
        ops.forEach(function (op, i) {
          var last = i === ops.length - 1;
          chain = chain.then(function () {
            return changeLine(op, last).then(function (state) {
              if (last) {
                renderSections(state);
                // ⚠️ MUST publish, unlike the deposit reconciler.
                // That one deliberately stays silent so its own writes cannot
                // re-trigger it. This one cannot afford to: removing a tray line
                // changes what DEPOSIT lines should exist, and the Pfand
                // reconciler only recomputes on cartUpdate. Staying silent left a
                // €6.00 Pfand line behind for a drink that had just been removed
                // — owner report, 2026-08-14.
                //
                // Safe against loops: our own subscriber re-runs once, re-reads
                // the freshly rendered limits, finds nothing over the limit
                // (we just fixed it) and stops. The limits cannot move, because
                // they are a pure function of the SINGLES.
                if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                  publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'pfanner-tray-guard',
                    cartData: state
                  });
                }
              }
              return state;
            });
          });
        });
        return chain;
      })
      .catch(function (e) {
        if (window.console) console.warn('[pfanner-tray-guard]', e);
      })
      .finally(function () {
        reconciling = false;
        // Show the notice against the freshly rendered cart.
        if (pending && pending.length) {
          var msgs = pending;
          pending = null;
          showNotice(msgs);
        }
      });
  }

  function scheduleEvaluate() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(evaluate, 140);
  }

  function init() {
    if (!limitsEl()) return; // no limits holder → stay inert

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () { scheduleEvaluate(); });
    }
    // Once on load: the cart may already hold an over-limit tray from an earlier
    // session, or stock may have moved since it was added.
    scheduleEvaluate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
